/* Per-channel PnL leaderboard — dashboards the winning/losing channels + open
   position counts so the user can pick which providers to keep and which to
   mute. Data comes from `/api/analytics/per_channel_pnl` (Mongo aggregation
   in one round-trip). */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { analytics, trades as tradesApi } from "@/lib/api";
import { Badge, cn } from "@/components/atoms";
import { Trophy, ArrowUpRight, ArrowDownRight, CheckCircle } from "@phosphor-icons/react";

const OV_CLOSED = ["closed_by_signal", "closed_by_tp", "closed_by_sl", "closed_by_auto_roi"];

/* Tiny inline sparkline: real cumulative realized_pnl for one channel's own
   closed trades, in close order - no fabricated "$100 start" curve, just the
   real $ path scaled to fit the card. */
function Sparkline({ points }) {
  if (points.length < 2) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const w = 220, h = 34;
  const d = points.map((v, i) => {
    const x = (i / (points.length - 1)) * w;
    const y = h - ((v - min) / span) * h;
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="mt-3">
      <path d={d} fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
    </svg>
  );
}

/* Gradient hero card per channel — real realized $ PnL / win-rate / trade
   count from the same /analytics/per_channel_pnl aggregation the table
   below uses, just surfaced as the headline visual instead of a table row. */
const HERO_GRADIENTS = [
  "linear-gradient(135deg,#4338CA,#6D5FF0)",
  "linear-gradient(135deg,#0F766E,#14B8A6)",
  "linear-gradient(135deg,#C2410C,#F59E0B)",
  "linear-gradient(135deg,#6D28D9,#A855F7)",
  "linear-gradient(135deg,#BE185D,#EC4899)",
  "linear-gradient(135deg,#1D4ED8,#38BDF8)",
];

export function ChannelHeroGrid({ onSelectChannel } = {}) {
  const q = useQuery({
    queryKey: ["per-channel-pnl"],
    queryFn: async () => (await analytics.perChannelPnl()).data,
    refetchInterval: 15_000,
  });
  const tradesQ = useQuery({
    queryKey: ["trades", "all"],
    queryFn: async () => (await tradesApi.list()).data,
    refetchInterval: 15_000,
  });
  const rows = q.data || [];

  const sparkByChannel = React.useMemo(() => {
    const closed = (tradesQ.data || [])
      .filter((t) => !t.paper_mode && t.closed_at && OV_CLOSED.includes(t.status))
      .sort((a, b) => new Date(a.closed_at) - new Date(b.closed_at));
    const running = {};
    const series = {};
    for (const t of closed) {
      const cid = t.channel_id;
      if (!cid) continue;
      running[cid] = (running[cid] || 0) + (t.realized_pnl || 0);
      (series[cid] ||= []).push(running[cid]);
    }
    return series;
  }, [tradesQ.data]);

  if (!rows.length) return null;

  // auto-fit instead of a fixed 4-col grid - with fewer than 4 channels a
  // fixed grid-cols-4 leaves a visible empty slot in the row; this stretches
  // however many real cards there are to fill the width.
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4" data-testid="channel-hero-grid">
      {rows.map((r, i) => {
        const clickable = !!onSelectChannel && r.channel_id !== "unassigned";
        return (
        <div
          key={r.channel_id}
          className={cn("card-hero p-5", clickable && "cursor-pointer hover:brightness-110 transition-[filter]")}
          style={{ background: HERO_GRADIENTS[i % HERO_GRADIENTS.length] }}
          data-testid={`channel-hero-${r.channel_id}`}
          role={clickable ? "button" : undefined}
          tabIndex={clickable ? 0 : undefined}
          onClick={clickable ? () => onSelectChannel(r.channel_id) : undefined}
          onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelectChannel(r.channel_id); } } : undefined}
        >
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold opacity-90 truncate pr-2">{r.channel_name}</p>
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <CheckCircle size={14} weight="bold" />
            </div>
          </div>
          <p className={cn("mono text-2xl font-bold mt-3")}>
            {r.realized_pnl >= 0 ? "+" : "-"}${Math.abs(r.realized_pnl).toFixed(2)}
          </p>
          <p className="text-xs opacity-85 mt-1">
            {r.wins + r.losses > 0 ? `${r.win_rate_pct}% win · ` : ""}{r.trades_count} trade{r.trades_count === 1 ? "" : "s"}
            {r.open_count > 0 ? ` · ${r.open_count} open` : ""}
          </p>
          <Sparkline points={sparkByChannel[r.channel_id] || []} />
        </div>
        );
      })}
    </div>
  );
}

export default function PnLLeaderboard() {
  const q = useQuery({
    queryKey: ["per-channel-pnl"],
    queryFn: async () => (await analytics.perChannelPnl()).data,
    refetchInterval: 15_000,
  });
  const rows = q.data || [];

  return (
    <div className="card-surface p-6" data-testid="pnl-leaderboard">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Trophy size={22} color="#D97706" weight="fill" />
          <div>
            <h3 className="font-display text-xl">Per-channel P&L</h3>
            <p className="text-xs text-slate-500">
              Realized PnL, win-rate, and open positions grouped by channel.
              Sorted by realized PnL — best performers on top.
            </p>
          </div>
        </div>
        {rows.length > 0 && (
          <Badge variant="info" data-testid="pnl-leaderboard-count">
            {rows.length} channel{rows.length === 1 ? "" : "s"}
          </Badge>
        )}
      </div>

      {q.isLoading && (
        <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>
      )}
      {!q.isLoading && rows.length === 0 && (
        <p className="text-sm text-slate-500 py-6 text-center"
           data-testid="pnl-leaderboard-empty">
          No trades yet. Once a channel executes signals, its performance
          shows up here.
        </p>
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-2">#</th>
                <th className="text-left py-3 px-2">Channel</th>
                <th className="text-left py-3 px-2">Exchange</th>
                <th className="text-right py-3 px-2">Trades</th>
                <th className="text-right py-3 px-2">Open</th>
                <th className="text-right py-3 px-2">Win rate</th>
                <th className="text-right py-3 px-2">W / L</th>
                <th className="text-right py-3 px-2">Realized PnL</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.channel_id}
                    data-testid={`pnl-row-${r.channel_id}`}
                    className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="py-3 px-2 text-slate-500 font-medium">{i + 1}</td>
                  <td className="py-3 px-2 font-medium">{r.channel_name}</td>
                  <td className="py-3 px-2 text-slate-600">{r.exchange}</td>
                  <td className="py-3 px-2 mono text-right">{r.trades_count}</td>
                  <td className="py-3 px-2 mono text-right">{r.open_count}</td>
                  <td className="py-3 px-2 mono text-right">
                    <span className={cn(
                      r.win_rate_pct >= 60 && "text-emerald-600 font-semibold",
                      r.win_rate_pct <= 40 && r.wins + r.losses > 0 && "text-red-600",
                    )}>
                      {r.wins + r.losses > 0 ? `${r.win_rate_pct}%` : "—"}
                    </span>
                  </td>
                  <td className="py-3 px-2 mono text-right text-slate-600">
                    {r.wins}/{r.losses}
                  </td>
                  <td className={cn(
                    "py-3 px-2 mono text-right font-semibold",
                    r.realized_pnl > 0 && "text-emerald-600",
                    r.realized_pnl < 0 && "text-red-600",
                  )}>
                    {r.realized_pnl > 0 && <ArrowUpRight size={12} className="inline mr-0.5" />}
                    {r.realized_pnl < 0 && <ArrowDownRight size={12} className="inline mr-0.5" />}
                    ${r.realized_pnl.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
